import React, { useEffect, useRef, useState } from "react";
import { Menu, X, Phone, Globe, ChevronDown, User } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { NAV_LINKS, COMPANY } from "../data/mock";
import { useLang } from "../i18n/LanguageContext";
import { useLocalizedPath } from "../i18n/langPath";
import { evUI } from "../data/eventUi";
import { LANGUAGES } from "../i18n/translations";
import { BrandLogo } from "./BrandLogo";
import { useCustomerAuth } from "../context/CustomerAuth";

const Logo = () => {
  const lp = useLocalizedPath();
  return (
    <Link to={lp("/")} className="flex items-center" data-testid="navbar-logo">
      <BrandLogo badge />
    </Link>
  );
};

const Flag = ({ f }) =>
  typeof f === "string" && (f.startsWith("/") || f.startsWith("http"))
    ? <img src={f} alt="" className="inline-block w-5 h-3.5 object-cover rounded-[2px] align-middle" />
    : <span>{f}</span>;

function LangSwitcher() {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} data-testid="lang-switcher"
        className="flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-[#F5B400] px-2 py-1.5 rounded-lg">
        <Globe className="w-4 h-4" /> <Flag f={current.flag} /> <span className="hidden sm:inline">{current.code.toUpperCase()}</span> <ChevronDown className="w-3.5 h-3.5" />
      </button>
      {open && (
        <div className="absolute right-0 mt-2 w-52 max-h-80 overflow-auto bg-white border border-slate-200 rounded-xl shadow-2xl py-1 z-50" data-testid="lang-menu">
          {LANGUAGES.map((l) => (
            <button key={l.code} onClick={() => { setLang(l.code); setOpen(false); }} data-testid={`lang-${l.code}`}
              className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2.5 hover:bg-slate-50 ${l.code === lang ? "text-[#F5B400] font-semibold" : "text-slate-700"}`}>
              <Flag f={l.flag} /> {l.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { t } = useLang();
  const lp = useLocalizedPath();
  const { lang } = useLang();
  const navLabel = (l) => (l.isEvent ? evUI(lang).nav : t(l.tkey));
  const { customer } = useCustomerAuth();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${scrolled ? "bg-white shadow-sm py-2.5" : "bg-white/95 backdrop-blur py-4"}`}>
      <div className="max-w-7xl mx-auto px-5 md:px-8 flex items-center justify-between gap-4">
        <Logo />
        <nav className="hidden lg:flex items-center gap-6">
          {NAV_LINKS.map((l) => (
            l.to ? (
              <Link key={l.tkey} to={lp(l.to)} className="text-sm font-medium text-slate-600 hover:text-[#F5B400] transition-colors" data-testid={`nav-${l.tkey}`}>{navLabel(l)}</Link>
            ) : (
              <a key={l.tkey} href={lp(l.href)} className="text-sm font-medium text-slate-600 hover:text-[#F5B400] transition-colors" data-testid={`nav-${l.tkey}`}>{navLabel(l)}</a>
            )
          ))}
        </nav>
        <div className="hidden lg:flex items-center gap-2">
          <LangSwitcher />
          <Link to={lp("/konto")} className="flex items-center gap-1.5 text-sm font-medium text-slate-700 hover:text-[#F5B400] px-2 py-1.5 rounded-lg" data-testid="navbar-account">
            <User className="w-4 h-4" /> <span>{customer ? (customer.first_name || t("acc_account")) : t("acc_login")}</span>
          </Link>
          <Button className="bg-[#F5B400] hover:bg-[#D99A00] text-neutral-900 rounded-full px-6 font-semibold" onClick={() => navigate(lp("/booking"))} data-testid="navbar-book-btn">
            {t("book_now")}
          </Button>
        </div>
        <div className="lg:hidden flex items-center gap-1">
          <LangSwitcher />
          <button className="text-slate-900 p-2" onClick={() => setOpen((v) => !v)} aria-label="Menu" data-testid="navbar-mobile-toggle">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden bg-white border-t border-slate-100 mt-2.5 shadow-lg">
          <div className="px-6 py-6 flex flex-col gap-4">
            {NAV_LINKS.map((l) => (
              l.to ? (
                <Link key={l.tkey} to={lp(l.to)} onClick={() => setOpen(false)} className="text-base font-medium text-slate-800">{navLabel(l)}</Link>
              ) : (
                <a key={l.tkey} href={lp(l.href)} onClick={() => setOpen(false)} className="text-base font-medium text-slate-800">{navLabel(l)}</a>
              )
            ))}
            <Link to={lp("/konto")} onClick={() => setOpen(false)} className="text-base font-medium text-slate-800 flex items-center gap-2" data-testid="navbar-account-mobile"><User className="w-4 h-4" /> {customer ? (customer.first_name || t("acc_account")) : t("acc_login")}</Link>
            <Button className="bg-[#F5B400] hover:bg-[#D99A00] text-neutral-900 rounded-full font-semibold mt-2" onClick={() => { setOpen(false); navigate(lp("/booking")); }}>{t("book_now")}</Button>
          </div>
        </div>
      )}
    </header>
  );
}
